// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo

import { MasterRider } from './MasterRider';
import { RiderId } from './RiderId';
import { Revenue } from './Revenue';
import { TourDay } from './TourDay';

import type { RevenueModelType } from 'common/models/flow/Revenue';
import type { MasterRiderModelType } from 'common/models/flow/MasterRider';
import type { RiderIdModelType } from 'common/models/flow/RiderId';
import type { TourDayModelType, WeekType } from './TourDay';

export type HistoricalDataModelType = {
    saleMadeRecently: boolean,
    currentBeatId: ?string,
    currentPjpType: ?string,
    riderId: ?RiderIdModelType,
    currentRiderId: string,
    rider: ?MasterRiderModelType,
    isNew: boolean,
    isDuplicateParty: boolean,
    recentRevenue: ?RevenueModelType,
    averageSkusSold: number,
    shouldKeep: boolean,
    validated: boolean,
    isFrozen: boolean,
    currentPjpDayInfo: TourDayModelType,
    currentPJPWeek: WeekType,
    isFrozenToRider: boolean,
    frozenRiderId?: string,
    isFrozenToBeat: boolean,
    frozenTourId?: string,
    serviceBySameRider: boolean
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class HistoricalData extends ImmutableModel {
    static fromJS(json: HistoricalDataModelType): HistoricalData {
        const state: Object = Object.assign({}, json);
        state.riderId = state.riderId
            ? RiderId.fromJS(state.riderId)
            : state.riderId;
        state.rider = state.rider
            ? MasterRider.fromJS(state.rider)
            : state.rider;
        state.recentRevenue = state.recentRevenue
            ? Revenue.fromJS(state.recentRevenue)
            : state.recentRevenue;
        state.currentPjpDayInfo = TourDay.fromJS(state.currentPjpDayInfo);
        return new this(Immutable.Map(state));
    }

    toJS(): HistoricalDataModelType {
        const js: HistoricalDataModelType = {
            saleMadeRecently: this.saleMadeRecently,
            currentBeatId: this.currentBeatId,
            currentPjpType: this.currentPjpType,
            riderId: this.riderId ? this.riderId.toJS() : this.riderId,
            currentRiderId: this.currentRiderId,
            rider: this.rider ? this.rider.toJS() : this.rider,
            isNew: this.isNew,
            isDuplicateParty: this.isDuplicateParty,
            recentRevenue: this.recentRevenue
                ? this.recentRevenue.toJS()
                : this.recentRevenue,
            averageSkusSold: this.averageSkusSold,
            shouldKeep: this.shouldKeep,
            validated: this.validated,
            isFrozen: this.isFrozen,
            currentPjpDayInfo: this.currentPjpDayInfo.toJS(),
            currentPJPWeek: this.currentPJPWeek,
            isFrozenToRider: this.isFrozenToRider,
            isFrozenToBeat: this.isFrozenToBeat,
            serviceBySameRider: this.serviceBySameRider
        };

        if (this.frozenRiderId != null) {
            js.frozenRiderId = this.frozenRiderId;
        }

        if (this.frozenTourId != null) {
            js.frozenTourId = this.frozenTourId;
        }

        return js;
    }

    get saleMadeRecently(): boolean {
        return this._state.get('saleMadeRecently');
    }

    setSaleMadeRecently(saleMadeRecently: boolean): this {
        return this.clone(
            this._state.set('saleMadeRecently', saleMadeRecently)
        );
    }

    get currentBeatId(): ?string {
        return this._state.get('currentBeatId');
    }

    setCurrentBeatId(currentBeatId: ?string): this {
        return this.clone(this._state.set('currentBeatId', currentBeatId));
    }

    get currentPjpType(): ?string {
        return this._state.get('currentPjpType');
    }

    setCurrentPjpType(currentPjpType: ?string): this {
        return this.clone(this._state.set('currentPjpType', currentPjpType));
    }

    get riderId(): ?RiderId {
        return this._state.get('riderId');
    }

    setRiderId(riderId: ?RiderId): this {
        return this.clone(this._state.set('riderId', riderId));
    }

    get currentRiderId(): string {
        return this._state.get('currentRiderId');
    }

    setCurrentRiderId(currentRiderId: string): this {
        return this.clone(this._state.set('currentRiderId', currentRiderId));
    }

    get rider(): ?MasterRider {
        return this._state.get('rider');
    }

    setRider(rider: ?MasterRider): this {
        return this.clone(this._state.set('rider', rider));
    }

    get isNew(): boolean {
        return this._state.get('isNew');
    }

    setIsNew(isNew: boolean): this {
        return this.clone(this._state.set('isNew', isNew));
    }

    get isDuplicateParty(): boolean {
        return this._state.get('isDuplicateParty');
    }

    setIsDuplicateParty(isDuplicateParty: boolean): this {
        return this.clone(
            this._state.set('isDuplicateParty', isDuplicateParty)
        );
    }

    get recentRevenue(): ?Revenue {
        return this._state.get('recentRevenue');
    }

    setRecentRevenue(recentRevenue: ?Revenue): this {
        return this.clone(this._state.set('recentRevenue', recentRevenue));
    }

    get averageSkusSold(): number {
        return this._state.get('averageSkusSold');
    }

    setAverageSkusSold(averageSkusSold: number): this {
        return this.clone(this._state.set('averageSkusSold', averageSkusSold));
    }

    get shouldKeep(): boolean {
        return this._state.get('shouldKeep');
    }

    setShouldKeep(shouldKeep: boolean): this {
        return this.clone(this._state.set('shouldKeep', shouldKeep));
    }

    get validated(): boolean {
        return this._state.get('validated');
    }

    setValidated(validated: boolean): this {
        return this.clone(this._state.set('validated', validated));
    }

    get isFrozen(): boolean {
        return this._state.get('isFrozen');
    }

    setIsFrozen(isFrozen: boolean): this {
        return this.clone(this._state.set('isFrozen', isFrozen));
    }

    get currentPjpDayInfo(): TourDay {
        return this._state.get('currentPjpDayInfo');
    }

    setCurrentPjpDayInfo(currentPjpDayInfo: TourDay): this {
        return this.clone(
            this._state.set('currentPjpDayInfo', currentPjpDayInfo)
        );
    }

    get currentPJPWeek(): WeekType {
        return this._state.get('currentPJPWeek');
    }

    setCurrentPJPWeek(currentPJPWeek: WeekType): this {
        return this.clone(this._state.set('currentPJPWeek', currentPJPWeek));
    }

    get isFrozenToRider(): boolean {
        return this._state.get('isFrozenToRider');
    }

    setIsFrozenToRider(isFrozenToRider: boolean): this {
        return this.clone(this._state.set('isFrozenToRider', isFrozenToRider));
    }

    get frozenRiderId(): ?string {
        return this._state.get('frozenRiderId');
    }

    setFrozenRiderId(frozenRiderId: ?string): this {
        return this.clone(this._state.set('frozenRiderId', frozenRiderId));
    }

    get isFrozenToBeat(): boolean {
        return this._state.get('isFrozenToBeat');
    }

    setIsFrozenToBeat(isFrozenToBeat: boolean): this {
        return this.clone(this._state.set('isFrozenToBeat', isFrozenToBeat));
    }

    get frozenTourId(): ?string {
        return this._state.get('frozenTourId');
    }

    setFrozenTourId(frozenTourId: ?string): this {
        return this.clone(this._state.set('frozenTourId', frozenTourId));
    }

    get serviceBySameRider(): boolean {
        return this._state.get('serviceBySameRider');
    }

    setServiceBySameRider(serviceBySameRider: boolean): this {
        return this.clone(
            this._state.set('serviceBySameRider', serviceBySameRider)
        );
    }
}
